import type {
  Order,
  Product,
  Expense,
  Customer,
  NewOrder,
  OrderLine,
  NewProduct,
  NewExpense,
  DbOrderLine,
  NewCustomer,
} from 'src/types/types';

import { supabase } from 'src/lib/supabase';

export const getAllExpenses = async (): Promise<Array<Expense>> => {
  const { data, error } = await supabase.from('expenses').select('*');

  if (error) {
    throw Error(error.message);
  }

  return data as Expense[];
};

export const getAllProducts = async (): Promise<Array<Product>> => {
  const { data, error } = await supabase.from('products').select('*');

  if (error) {
    throw Error(error.message);
  }

  const mappedData = (data as Product[]).map((p) => {
    if (p.replacementProduct) {
      return {
        ...p,
        replacementProduct: data.find((d) => d.id === p.replacementProduct),
      };
    } else {
      return p;
    }
  });

  return mappedData as Product[];
};

// Pakker en rå `orders`-række (flade customer*-snapshot-kolonner + den legacy `customer`-FK)
// om til app-formen: et nested `customer`-snapshot-objekt plus en ren opslags-`customerId`.
const toOrder = (row: any): Order => {
  const {
    customer: customerId,
    customerName,
    customerPhone,
    customerAddress,
    customerCompany,
    customerCompanyCvr,
    ...rest
  } = row;

  return {
    ...rest,
    customer: {
      name: customerName,
      phone: customerPhone,
      address: customerAddress ?? undefined,
      company: customerCompany ?? undefined,
      companyCvr: customerCompanyCvr ?? undefined,
    },
    customerId: customerId ?? null,
  } as Order;
};

export const getAllOrders = async (): Promise<Array<Order>> => {
  // Bevidst IKKE `!inner` på orderLines/products: et inner join dropper hele ordren, hvis den
  // mangler ordrelinjer, eller hvis en linje peger på et siden slettet produkt — det gjorde
  // rigtige ordrer usynlige i ordrelisten uden nogen fejlmeddelelse. Med et almindeligt join
  // får en ordre uden linjer bare `orderLines: []`, og en linje med et slettet produkt får
  // `product: undefined` i stedet for at forsvinde.
  const { data, error } = await supabase
    .from('orders')
    .select('*, orderLines: orderLines(*, product: products(*))')
    // Ekskluderer forladte/ufuldstændige kladde-rækker (uden status), som tidligere blev
    // filtreret væk som en bivirkning af det fjernede `customers!inner`-join.
    .not('status', 'is', null);

  if (error) {
    throw Error(error.message);
  }

  return (data ?? []).map(toOrder);
};

export const getAllCustomers = async (): Promise<Array<Customer>> => {
  const { data, error } = await supabase.from('customers').select('*');

  if (error) {
    throw Error(error.message);
  }

  return data as Customer[];
};

export const createOrder = async (order: NewOrder): Promise<NewOrder> => {
  const { customerId, ...rest } = order;
  const row = { ...rest, customer: customerId ?? null };

  const { data, error } = await supabase.from('orders').upsert(row).select();

  if (error) {
    throw Error(error.message);
  }

  return data[0];
};

export const createOrderLines = async (orderLines: DbOrderLine[]): Promise<void> => {
  const { data, error } = await supabase.from('orderLines').insert(orderLines).select();

  if (error) {
    throw Error(error.message);
  }

  return data[0];
};
export const updateOrderLines = async (orderLines: OrderLine[]): Promise<void> => {
  const { data, error } = await supabase.from('orderLines').upsert(orderLines).select();

  if (error) {
    throw Error(error.message);
  }

  return data[0];
};

export const deleteExpense = async (expenseId: string) => {
  const { error } = await supabase.from('expenses').delete().eq('id', expenseId);
  if (error) {
    throw Error(error.message);
  }
};

export const deleteOrder = async (orderId: string) => {
  const { error } = await supabase.from('orders').delete().eq('id', orderId);
  if (error) {
    throw Error(error.message);
  }
};

export const deleteOrderLines = async (orderId: string) => {
  const { error } = await supabase.from('orderLines').delete().eq('"order"', orderId);
  if (error) {
    throw Error(error.message);
  }
};

export const getOrder = async (id: string): Promise<Order> => {
  // Fetch the order
  const { data: orderData, error: orderError } = await supabase
    .from('orders')
    .select('*')
    .eq('id', id);

  if (orderError || !orderData || orderData.length === 0) {
    throw new Error(orderError?.message || 'Order not found');
  }

  const order = orderData[0];

  // Ordren bærer nu selv sit kunde-snapshot (customerName/customerPhone/...), så der er ikke
  // længere brug for et separat opslag i `customers` her.
  const { data: orderLinesData, error: orderLinesError } = await supabase
    .from('orderLines')
    .select('*')
    .eq('"order"', order.id);

  if (orderLinesError) {
    throw new Error(orderLinesError.message);
  }

  const orderLines = orderLinesData ?? [];

  // Extract product IDs from orderLines
  const productIds = [...new Set(orderLines.map((line) => line.product))];

  // Fetch products
  const { data: productsData, error: productsError } = await supabase
    .from('products')
    .select('*')
    .in('id', productIds);

  if (productsError) {
    throw new Error(productsError.message);
  }

  const productsById = new Map(productsData.map((p) => [p.id, p]));

  // Attach product to each orderLine
  const enrichedOrderLines = orderLines.map((line, i) => ({
    ...line,
    product: productsById.get(line.product),
    orderLineId: i + 1,
  }));

  return toOrder({ ...order, orderLines: enrichedOrderLines });
};

export const createProduct = async (product: NewProduct) => {
  const { data, error } = await supabase.from('products').upsert(product).select();

  if (error) {
    throw new Error(error.message);
  } else {
    return data[0] as Product;
  }
};

export const createExpense = async (expense: NewExpense) => {
  const { data, error } = await supabase.from('expenses').upsert(expense).select();

  if (error) {
    throw new Error(error.message);
  } else {
    return data[0] as Expense;
  }
};

export const updateOrder = async (
  orderId: string,
  field: Exclude<keyof Order, 'customer' | 'customerId' | 'orderLines'>,
  value: any,
  revolutOrderId?: string
) => {
  const { data, error } = await supabase
    .from('orders')
    .update({ [field]: value, revolutOrderId })
    .eq('id', orderId)
    .select();

  if (error) {
    throw new Error(error.message);
  } else {
    return data;
  }
};

export const createCustomer = async (customer: NewCustomer) => {
  const { data, error } = await supabase.from('customers').upsert(customer).select();

  if (error) {
    throw new Error(error.message);
  } else {
    return data[0] as Customer;
  }
};
